// TODO: only allow one daily report for a given day (like schedules), and reformat the user input date (so 09/5/2025 goes to 09/06/2025)

import { connect } from 'react-redux';
import { useState, useEffect, useRef, Fragment, } from 'react';

import { SET_NEW_DAILY_REPORT_DIALOGUE_ID, GET_DAILY_REPORT_SUMMARIES, SET_EDIT_DAILY_REPORT_DIALOGUE_ID, DateStringMMDDYYYY } from '../actions/types';
import { setPath, siteboardAction } from '../actions/defaultActions';
import { keyCountInObject } from '../Util';
import PoweredByBar from './PoweredByBar';
import SortableTable from './SortableTable';

// icon imports
import Logo from '../SiteBoard_Logo';
import PrintIcon from '../icons/printIcon';
import EmailIcon from '../icons/emailIcon';
import MessagesIcon from '../icons/messagesIcon';
import DotsIcon from '../icons/dotsIcon';
import GlobeIcon from '../icons/globeIcon';
import AddIcon from '../icons/addIcon';
import NotepadIcon from '../icons/notepadIcon';
import ExportIcon from '../icons/exportIcon';
import SettingsIcon from '../icons/settingsIcon';
import EditIcon from '../icons/editIcon';
import { Hash, MapPin, Ellipsis, Send, CircleCheck, Eye, FileText, } from 'lucide-react';

const DailyReports = ({ _projects, _dailyReportSummaries, _dailyReports, setPath, siteboardAction, projectId }) => {
  const selectedProject = projectId in _projects ? _projects[projectId] : {};
  const sortedDailyReportSummaries = [];
  const [isLoadingScheduleSummaries, setIsLoadingScheduleSummaries] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().getTime());

  useEffect(() => {
    const onSuccess = () => { setIsLoadingScheduleSummaries(false); };
    const onFailure = () => {};
    siteboardAction(GET_DAILY_REPORT_SUMMARIES, {projectId: projectId}, onSuccess, onFailure);
  }, [projectId, siteboardAction]);

  // use the full version of the daily report if we have it, the summary is just a reduced data substitue
  for(const key in _dailyReports) {
    if(_dailyReports[key].projectId === projectId) {
      sortedDailyReportSummaries.push(_dailyReports[key]);
    }
  }
  for(const key in _dailyReportSummaries) {
    if(_dailyReportSummaries[key].projectId === projectId && !(key in _dailyReports)) {
      sortedDailyReportSummaries.push(_dailyReportSummaries[key]);
    }
  }
  sortedDailyReportSummaries.sort((one, two) => {
    try {
      const oneDate = DateStringMMDDYYYY.toDateObject(one.date);
      const twoDate = DateStringMMDDYYYY.toDateObject(two.date);
      return twoDate.getTime() - oneDate.getTime();
    } catch(error) {
      return 0;
    }
  });

  const dailyProjectData = [];

  return (
    <div className={'flex flex-col w-full h-full bg-black/90 overflow-y-auto'}>

      {/* Title bar */}
      <div className={'flex justify-between items-center gap-x-8 p-2 whitespace-nowrap lg:whitespace-normal'}>
        {/* Project name */}
        <div className={`flex flex-col`}>
          <div className='w-full'>
            <div className='inline text-[1.6em] sm:text-[2em] text-[#f9a928] font-bold leading-[1em] tracking-tight'>{selectedProject.name}</div>
            {(typeof selectedProject.jobNumber === 'string' && selectedProject.jobNumber.length > 0) &&
              <div className='inline text-[1em] text-white/70 leading-[1em] whitespace-nowrap'> <Hash size={16} className='inline relative top-[-2px]' />{selectedProject.jobNumber}</div>
            }
          </div>
          {(typeof selectedProject.address === 'string' && selectedProject.address.length > 0) &&
            <div className='hidden lg:block text-white/70'>
              <div className='inline-block align-middle'><MapPin size={16} /></div> <p className='inline-block text-[1em] whitespace-nowrap'>{(selectedProject.address || '').replaceAll(/\n/g, ', ').replaceAll(/,+/g, ',')}</p>
            </div>
          }
        </div>
        {/* Daily Reports Title */}
        <div className='grow hidden xl:flex justify-center items-center gap-x-[8px]'>
          <div className='sm:hidden'><FileText size='24' className='text-emerald-500' /></div>
          <div className='hidden sm:block'><FileText size='1.8em' className='text-emerald-500' /></div>
          <p className='text-[1.4em] font-normal text-[#fff] whitespace-nowrap'>Daily Report</p>
        </div>
      </div>

      <div className={'grow flex min-h-0 bg-[#1e1e1e]'}>
        <div className='flex flex-col grow'>
          {/* main area */}
          {isLoadingScheduleSummaries &&
            <div className='grow flex flex-col justify-center gap-y-1 p-1'>
              <div className='flex rounded-[10px] py-6 px-10 items-center justify-center font-semibold text-white text-[1.5em] gap-x-4'>
                <div className='animate-[spin_1s_linear_infinite]'>
                  <Logo size={48} />
                </div>
                <p className='select-none'>Loading Daily Reports</p>
              </div>
            </div>
          }
          {!isLoadingScheduleSummaries &&
            <div className='grow flex flex-col justify-start items-start gap-y-10 p-1'>

              <DailyReportDatePicker
                value={selectedDate}
                onChange={(timestamp) => { setSelectedDate(timestamp); }}
              />

              <SortableTable 
                columns={[
                  { 
                    key: 'isOnSite',
                    label: "On Site",
                    width:'10%',
                    render: (value, row) => value,
                  },
                  { 
                    key: 'responsible',
                    label: "Responsible",
                    width:'20%',
                    render: (value, row) => value,
                  },
                  { 
                    key: 'activity',
                    label: "Activity",
                    width:'20%',
                    render: (value, row) => value,
                  },
                  { 
                    key: 'superNotes',
                    label: "Superintendent Notes",
                    width:'40%',
                    render: (value, row) => value,
                    sortValue: (value, row) => value,
                  },
                ]}
                data={dailyProjectData}
              />

              {/* list of reports */}
              <div className='w-full grid grid-cols-[1fr_4fr_4fr_4fr_4fr_4fr_4fr] gap-y-1 text-white font-semibold'>
                {/* list of reports header */}
                <div></div>
                <div className='flex justify-center items-center text-[1.2em] text-[#a59e9e]'>Date</div>
                <div className='flex justify-center items-center text-[1.2em] text-[#a59e9e]'>Assignee</div>
                <div className='flex justify-center items-center text-[1.2em] text-[#a59e9e]'>Weather</div>
                <div className='flex justify-center items-center text-[1.2em] text-[#a59e9e]'>Delays</div>
                <div className='flex justify-center items-center text-[1.2em] text-[#a59e9e]'>Inspections</div>
                <div className='flex justify-center items-center text-[1.2em] text-[#a59e9e]'>Status</div>
                {/* header separator line */}
                <div className='col-span-7 h-[1px] w-full bg-[#a59e9e]'></div>
                {/* daily report summary items */}
                {sortedDailyReportSummaries.map((dailyReportSummary, index) => (
                  <DailyReportLine 
                    key={index} 
                    setPath={setPath}
                    dailyReportSummary={dailyReportSummary} 
                    projectId={projectId}
                    siteboardAction={siteboardAction}
                  />
                  /*<Fragment key={index}>
                    <div className='flex justify-center items-center'><input type='checkbox' className='w-[1.2em] h-[1.2em]' /></div>
                    <div className='flex justify-center items-center'>{dailyReportSummary.date}</div>
                    <div className='flex justify-center items-center'>{dailyReportSummary.assignee}</div>
                    <div className='flex justify-center items-center'>{dailyReportSummary.weatherCondition} / {dailyReportSummary.weatherTempF}&deg;F</div>
                    <div className='flex justify-center items-center'>{dailyReportSummary.hasDelays === true ? 'YES' : 'NO'}</div>
                    <div className='flex justify-center items-center'>{dailyReportSummary.hasInspections === true ? 'YES' : 'NO'}</div>
                    <div className='flex justify-center items-center'>{dailyReportSummary.isPublished === true ? 'Published' : 'Draft'}</div>
                    <button 
                      className='flex justify-center items-center'
                      onClick={() => {
                        setPath('projectId#'+projectId+'#dailyReportId#'+dailyReportSummary.id);
                      }}
                    >
                      <EditIcon />
                    </button>
                  </Fragment>*/
                ))}
              </div>
            </div>
          }
          <PoweredByBar />
        </div>
      </div>
    </div>
  );
}

const DailyReportLine = ({ dailyReportSummary, projectId, setPath, siteboardAction }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Fragment>
      <div className='flex justify-center items-center'><input type='checkbox' className='w-[1.2em] h-[1.2em]' /></div>
      <div 
        className={`flex justify-center items-center cursor-pointer py-2 ${isHovered ? 'bg-[#151515]' : ''}`}
        onMouseEnter={() => { setIsHovered(true); }}
        onMouseLeave={() => { setIsHovered(false); }} 
        //onClick={() => { setPath('projectId#'+projectId+'#dailyReportId#'+dailyReportSummary.id); }}
        onClick={() => { siteboardAction(SET_EDIT_DAILY_REPORT_DIALOGUE_ID, { dailyReportId: dailyReportSummary.id }); }}
        title={`View report for ${dailyReportSummary.date}`}
      >{dailyReportSummary.date}</div>
      <div 
        className={`flex justify-center items-center cursor-pointer py-2 ${isHovered ? 'bg-[#151515]' : ''}`}
        onMouseEnter={() => { setIsHovered(true); }}
        onMouseLeave={() => { setIsHovered(false); }} 
        //onClick={() => { setPath('projectId#'+projectId+'#dailyReportId#'+dailyReportSummary.id); }}
        onClick={() => { siteboardAction(SET_EDIT_DAILY_REPORT_DIALOGUE_ID, { dailyReportId: dailyReportSummary.id }); }}
        title={`View report for ${dailyReportSummary.date}`}
      >{dailyReportSummary.assignee}</div>
      <div 
        className={`flex justify-center items-center cursor-pointer py-2 ${isHovered ? 'bg-[#151515]' : ''}`}
        onMouseEnter={() => { setIsHovered(true); }}
        onMouseLeave={() => { setIsHovered(false); }} 
        //onClick={() => { setPath('projectId#'+projectId+'#dailyReportId#'+dailyReportSummary.id); }}
        onClick={() => { siteboardAction(SET_EDIT_DAILY_REPORT_DIALOGUE_ID, { dailyReportId: dailyReportSummary.id }); }}
        title={`View report for ${dailyReportSummary.date}`}
      >{dailyReportSummary.weatherCondition} / {dailyReportSummary.weatherTempF}&deg;F</div>
      <div 
        className={`flex justify-center items-center cursor-pointer py-2 ${isHovered ? 'bg-[#151515]' : ''}`}
        onMouseEnter={() => { setIsHovered(true); }}
        onMouseLeave={() => { setIsHovered(false); }} 
        //onClick={() => { setPath('projectId#'+projectId+'#dailyReportId#'+dailyReportSummary.id); }}
        onClick={() => { siteboardAction(SET_EDIT_DAILY_REPORT_DIALOGUE_ID, { dailyReportId: dailyReportSummary.id }); }}
        title={`View report for ${dailyReportSummary.date}`}
      >{dailyReportSummary.hasDelays === true ? 'YES' : 'NO'}</div>
      <div 
        className={`flex justify-center items-center cursor-pointer py-2 ${isHovered ? 'bg-[#151515]' : ''}`}
        onMouseEnter={() => { setIsHovered(true); }}
        onMouseLeave={() => { setIsHovered(false); }} 
        //onClick={() => { setPath('projectId#'+projectId+'#dailyReportId#'+dailyReportSummary.id); }}
        onClick={() => { siteboardAction(SET_EDIT_DAILY_REPORT_DIALOGUE_ID, { dailyReportId: dailyReportSummary.id }); }}
        title={`View report for ${dailyReportSummary.date}`}
      >{dailyReportSummary.hasInspections === true ? 'YES' : 'NO'}</div>
      <div 
        className={`flex justify-center items-center cursor-pointer py-2 ${isHovered ? 'bg-[#151515]' : ''}`}
        onMouseEnter={() => { setIsHovered(true); }}
        onMouseLeave={() => { setIsHovered(false); }} 
        //onClick={() => { setPath('projectId#'+projectId+'#dailyReportId#'+dailyReportSummary.id); }}
        onClick={() => { siteboardAction(SET_EDIT_DAILY_REPORT_DIALOGUE_ID, { dailyReportId: dailyReportSummary.id }); }}
        title={`View report for ${dailyReportSummary.date}`}
      >{dailyReportSummary.isPublished === true ? 'Published' : 'Draft'}</div>
    </Fragment>
  );
};


const DailyReportDatePicker = ({
  value,
  onChange,
  className = '',
}) => {
  const inputRef = useRef(null);
  const date = new Date(value);

  const formattedDate = new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);

  // Use local date parts to avoid UTC shifting the selected day.
  const inputValue = [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');

  const changeDay = (amount) => {
    const nextDate = new Date(value);
    nextDate.setDate(nextDate.getDate() + amount);
    onChange(nextDate.getTime());
  };

  const openCalendar = () => {
    const input = inputRef.current;

    if (typeof input?.showPicker === 'function') {
      input.showPicker();
    } else {
      input?.focus();
      input?.click();
    }
  };

  const buttonClassName = `
    flex shrink-0 items-center justify-center
    self-stretch px-3
    text-neutral-100
    transition-colors duration-150
    hover:bg-neutral-700/50 hover:text-white
    active:bg-neutral-700
    focus-visible:outline-none
    focus-visible:ring-2 focus-visible:ring-inset
    focus-visible:ring-neutral-500
  `;

  return (
    <div
      className={`
        inline-flex h-12 max-w-full items-center overflow-hidden
        rounded-lg border border-neutral-700/60
        bg-neutral-800 text-neutral-100
        ${className}
      `}
    >
      <div className='relative flex self-stretch'>
        <button
          type='button'
          onClick={openCalendar}
          aria-label='Choose report date'
          className={buttonClassName}
        >
          <svg
            width='25'
            height='25'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='1.5'
            strokeLinecap='round'
            strokeLinejoin='round'
            aria-hidden='true'
          >
            <rect x='3' y='5' width='18' height='16' rx='2' />
            <path d='M16 3v4M8 3v4M3 10h18' />
          </svg>
        </button>

        <input
          ref={inputRef}
          type='date'
          value={inputValue}
          onChange={(event) => {
            if (!event.target.value) return;

            const [year, month, day] = event.target.value
              .split('-')
              .map(Number);

            onChange(new Date(year, month - 1, day).getTime());
          }}
          aria-label='Report date'
          tabIndex={-1}
          className='pointer-events-none absolute bottom-0 left-0 h-px w-px opacity-0'
        />
      </div>

      <div className='h-7 w-px shrink-0 bg-neutral-700/60' />

      <button
        type='button'
        onClick={openCalendar}
        className={`
          ${buttonClassName}
          min-w-0 flex-1 px-3 text-[1.1rem] font-bold
          sm:px-5 sm:text-[1.3rem]
        `}
        aria-label={`Choose report date, ${formattedDate}`}
      >
        <span className='truncate whitespace-nowrap'>
          {formattedDate}
        </span>
      </button>

      <div className='h-7 w-px shrink-0 bg-neutral-700/60' />

      <button
        type='button'
        onClick={() => changeDay(-1)}
        aria-label='Previous day'
        className={buttonClassName}
      >
        <svg
          width='22'
          height='22'
          viewBox='0 0 24 24'
          fill='none'
          stroke='currentColor'
          strokeWidth='1.8'
          strokeLinecap='round'
          strokeLinejoin='round'
          aria-hidden='true'
        >
          <path d='m15 18-6-6 6-6' />
        </svg>
      </button>

      <div className='h-7 w-px shrink-0 bg-neutral-700/60' />

      <button
        type='button'
        onClick={() => changeDay(1)}
        aria-label='Next day'
        className={buttonClassName}
      >
        <svg
          width='22'
          height='22'
          viewBox='0 0 24 24'
          fill='none'
          stroke='currentColor'
          strokeWidth='1.8'
          strokeLinecap='round'
          strokeLinejoin='round'
          aria-hidden='true'
        >
          <path d='m9 18 6-6-6-6' />
        </svg>
      </button>
    </div>
  );
};

const mapStateToProps = state => { return { // define redux state that will be passed in as props
  _projects: state.serverSynced.projects,
  _dailyReports: state.serverSynced.dailyReports,
  _dailyReportSummaries: state.serverSynced.dailyReportSummaries,
}};
export default connect(mapStateToProps, {setPath, siteboardAction})(DailyReports);
